import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { MapContainer, TileLayer, Circle, CircleMarker, Popup, Marker } from 'react-leaflet';
import L from 'leaflet';
import MarkerClusterGroup from './MarkerClusterGroup';
import { useAdmin } from '../context/AdminContext';

import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import './markerClusterStyles.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

const AdminModeratorCoverageMap = () => {
  const { getAuthHeaders, adminLogout } = useAdmin();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [moderators, setModerators] = useState([]);
  const [reports, setReports] = useState([]);

  const createClusterIcon = (cluster) => {
    const childCount = cluster.getChildCount();
    let level1Count = 0;
    let level2Count = 0;
    let level3Count = 0;
    let crimeReportCount = 0;

    const markers = cluster.getAllChildMarkers();
    markers.forEach(marker => {
      const report = marker.options.report;
      if (report) {
        if (report.type === 'level_1') level1Count++;
        else if (report.type === 'level_2') level2Count++;
        else if (report.type === 'level_3') level3Count++;
        else if (report.type === 'crime_report') crimeReportCount++;
      }
    });

    let clusterClass = 'marker-cluster-low';
    const severityCount = level1Count + level2Count + level3Count;
    if (crimeReportCount > 0 && severityCount === 0) {
      clusterClass = 'marker-cluster-crime-report';
    }
    if (level3Count > 0) {
      clusterClass = 'marker-cluster-high';
    } else if (level2Count > 0) {
      clusterClass = 'marker-cluster-medium';
    }

    return L.divIcon({
      html: `<div><span>${childCount}</span></div>`,
      className: `marker-cluster ${clusterClass}`,
      iconSize: L.point(40, 40)
    });
  };

  const createSeverityIcon = (type, timestamp = null) => {
    const severityColors = {
      'level_1': { main: '#28a745' },
      'level_2': { main: '#ffc107' },
      'level_3': { main: '#dc3545' },
      'default': { main: '#6c757d' }
    };

    const colors = severityColors[type] || severityColors.default;
    const isRecent = timestamp && (Date.now() - new Date(timestamp).getTime()) < 24 * 60 * 60 * 1000;
    let className = `custom-icon severity-icon-3d ${type || 'default'}`;
    if (isRecent) {
      className += ' recent-marker';
    }

    const html = `
      <div style="
        width: 30px;
        height: 30px;
        background: ${colors.main};
        border: 3px solid white;
        border-radius: 50%;
        box-shadow: 0 2px 5px rgba(0,0,0,0.5);
      "></div>
    `;

    return L.divIcon({
      className,
      html,
      iconSize: [30, 30],
      iconAnchor: [15, 15],
      popupAnchor: [0, -15]
    });
  };

  const mapCenter = useMemo(() => {
    const firstReport = (reports || []).find(r => r?.position && Number.isFinite(Number(r.position.lat)) && Number.isFinite(Number(r.position.lng)));
    if (firstReport) return [Number(firstReport.position.lat), Number(firstReport.position.lng)];
    const first = (moderators || []).find(m => Number.isFinite(Number(m.centerLat)) && Number.isFinite(Number(m.centerLng)));
    if (first) return [Number(first.centerLat), Number(first.centerLng)];
    return [37.7749, -122.4194];
  }, [moderators, reports]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const headers = getAuthHeaders();
        const [coverageRes, reportsRes] = await Promise.all([
          axios.get(`${API_URL}/api/admin/moderator-coverage`, { headers }),
          axios.get(`${API_URL}/api/reports/all?limit=2000`, { headers })
        ]);

        setModerators(coverageRes.data.moderators || []);
        setReports(Array.isArray(reportsRes.data) ? reportsRes.data : (reportsRes.data?.reports || []));
      } catch (err) {
        if (err.response?.status === 401 || err.response?.status === 403) {
          adminLogout();
          return;
        }
        setError(err.response?.data?.message || 'Failed to load moderator coverage');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [getAuthHeaders, adminLogout]);

  return (
    <div style={{ height: '100vh', width: '100%', display: 'flex', flexDirection: 'column' }}>
      <div style={{ padding: '12px 14px', borderBottom: '1px solid #e8e8e8', background: 'white' }}>
        <div style={{ fontWeight: 900, fontSize: '18px' }}>Moderator Coverage Map</div>
        <div style={{ color: '#666', fontSize: '12px', marginTop: '4px' }}>
          Full incident map with moderator coverage circles overlaid.
        </div>
      </div>

      {error && (
        <div style={{ background: '#ffe6e6', border: '1px solid #ffb3b3', padding: '10px 12px', borderRadius: '10px', marginBottom: '12px' }}>
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ padding: '12px' }}>Loading…</div>
      ) : (
        <div style={{ flex: 1, minHeight: 0 }}>
          <MapContainer center={mapCenter} zoom={11} style={{ height: '100%', width: '100%' }}>
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />

            {Array.isArray(reports) && reports.length > 0 && (
              <MarkerClusterGroup
                chunkedLoading={true}
                maxClusterRadius={80}
                spiderfyOnMaxZoom={true}
                disableClusteringAtZoom={19}
                showCoverageOnHover={false}
                zoomToBoundsOnClick={true}
                iconCreateFunction={createClusterIcon}
              >
                {reports
                  .filter(r => r && r.position && typeof r.position.lat === 'number' && typeof r.position.lng === 'number')
                  .map((r) => (
                    <Marker
                      key={r.id}
                      position={[r.position.lat, r.position.lng]}
                      icon={createSeverityIcon(r.type, r.timestamp)}
                      report={r}
                    >
                      <Popup autoPan={true} autoPanPadding={[50, 50]} closeOnClick={false} autoClose={false} keepInView={true}>
                        <div style={{ minWidth: '240px' }}>
                          <div style={{ fontWeight: 800, marginBottom: '6px' }}>{r.type}</div>
                          {r.address ? <div style={{ fontSize: '12px', color: '#555', marginBottom: '6px' }}>{r.address}</div> : null}
                          {r.description ? <div style={{ fontSize: '13px', color: '#222' }}>{r.description}</div> : null}
                          {r.timestamp ? <div style={{ fontSize: '11px', color: '#666', marginTop: '8px' }}>{new Date(r.timestamp).toLocaleString()}</div> : null}
                        </div>
                      </Popup>
                    </Marker>
                  ))}
              </MarkerClusterGroup>
            )}

            {(moderators || [])
              .filter(m => Number.isFinite(Number(m.centerLat)) && Number.isFinite(Number(m.centerLng)) && Number.isFinite(Number(m.radiusMeters)) && Number(m.radiusMeters) > 0)
              .map(m => {
                const center = [Number(m.centerLat), Number(m.centerLng)];
                const radius = Number(m.radiusMeters);
                const displayName = (m.name || '').trim();
                const displayEmail = (m.userEmail || '').trim();
                const title = displayName && displayEmail
                  ? `${displayName} (${displayEmail})`
                  : (displayName || displayEmail || m.userId);
                return (
                  <React.Fragment key={m.userId}>
                    <Circle
                      center={center}
                      radius={radius}
                      pathOptions={{ color: '#2C341B', weight: 2, fillColor: '#97AC82', fillOpacity: 0.18 }}
                    />
                    <CircleMarker
                      center={center}
                      radius={6}
                      pathOptions={{ color: '#2C341B', weight: 2, fillColor: '#2C341B', fillOpacity: 0.85 }}
                    >
                      <Popup>
                        <div style={{ minWidth: '220px' }}>
                          <div style={{ fontWeight: 900, marginBottom: '6px' }}>{title}</div>
                          <div style={{ fontSize: '12px', color: '#444' }}>
                            <div><strong>User ID:</strong> {m.userId}</div>
                            {displayName ? <div><strong>Name:</strong> {displayName}</div> : null}
                            {displayEmail ? <div><strong>Email:</strong> {displayEmail}</div> : null}
                            {m.chapter ? <div><strong>Chapter:</strong> {m.chapter}</div> : null}
                            {m.city ? <div><strong>City:</strong> {m.city}</div> : null}
                            <div><strong>Radius:</strong> {Math.round(radius)} m</div>
                            <div>
                              <strong>Center:</strong>{' '}
                              <a
                                href={`https://www.google.com/maps?q=${encodeURIComponent(`${Number(m.centerLat).toFixed(6)},${Number(m.centerLng).toFixed(6)}`)}`}
                                target="_blank"
                                rel="noreferrer"
                              >
                                {Number(m.centerLat).toFixed(5)}, {Number(m.centerLng).toFixed(5)}
                              </a>
                            </div>
                          </div>
                        </div>
                      </Popup>
                    </CircleMarker>
                  </React.Fragment>
                );
              })}
          </MapContainer>
        </div>
      )}
    </div>
  );
};

export default AdminModeratorCoverageMap;
